import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '../data/translations';
import { translationsExtra } from '../data/translations2';

const LanguageContext = createContext(null);

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used within LanguageProvider');
  return ctx;
};

// Deep merge helper: `extra` overrides/extends `base`.
const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);
const deepMerge = (base, extra) => {
  const out = Array.isArray(base) ? [...base] : { ...base };
  Object.keys(extra || {}).forEach((k) => {
    if (isObj(out[k]) && isObj(extra[k])) out[k] = deepMerge(out[k], extra[k]);
    else out[k] = extra[k];
  });
  return out;
};

const MERGED = {
  de: deepMerge(translations.de, translationsExtra.de),
  en: deepMerge(translations.en, translationsExtra.en),
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('mk_lang') || 'de';
    return 'de';
  });

  useEffect(() => {
    localStorage.setItem('mk_lang', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (path) => {
      const parts = path.split('.');
      let cur = MERGED[lang];
      for (const p of parts) cur = cur == null ? undefined : cur[p];
      if (cur === undefined) {
        // Primary-language fallback is German.
        let fb = MERGED.de;
        for (const p of parts) fb = fb == null ? undefined : fb[p];
        return fb === undefined ? path : fb;
      }
      return cur;
    },
    [lang]
  );

  const toggleLang = () => setLang((l) => (l === 'de' ? 'en' : 'de'));

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
